// Public Storefront — Graphy-style featured courses section (for landing),
// course listing page, and course detail page.
import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ShoppingCart, Zap, Star, Users, Clock, PlayCircle, ArrowRight,
  CheckCircle2, Award, MessageCircle, ChevronRight, GraduationCap, X, Download,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { resolveImg } from "@/lib/imageUtils";
import { useAuth } from "@/context/AuthContext";
import { useSitePageVisibility } from "@/context/SitePageVisibilityContext";
import { useWhatsAppUrl } from "@/context/SiteConfigContext";
import { usePageTitle } from "@/hooks/usePageTitle";
import { Logo } from "@/components/Logo";
import {
  addToGuestCart, removeFromGuestCart, getGuestCart, getGuestCartCount,
  getAppliedCoupon, setAppliedCoupon, setCheckoutRedirect, clearGuestCart,
} from "@/lib/guestCart";

// -----------------------------------------------------------
// Public API helpers (no /api prefix — lib/api already does /api)
// -----------------------------------------------------------
const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

// Get axios/api directly for public calls (no auth needed) — same client works
async function publicFetch(url) {
  const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api${url}`);
  const d = await res.json();
  if (!d.success) throw new Error(d.message || "Request failed");
  return d.data;
}

// -----------------------------------------------------------
// COURSE CARD (Graphy-style)
// -----------------------------------------------------------
export function CourseCard({ course }) {
  const nav = useNavigate();
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);

  const buyNow = async () => {
    if (course.launching_soon) return;
    if (course.contact_only) {
      nav(`/courses/${course.slug}#enquiry`);
      return;
    }
    if (!user) {
      // GUEST FLOW: add to guest cart + go to /cart, ask login only at checkout
      const r = addToGuestCart(course);
      if (!r.added && r.reason === "duplicate") toast("This course is already in your cart.");
      else if (r.added) toast.success("Course added to cart.");
      setCheckoutRedirect(true);
      nav("/cart");
      return;
    }
    setBusy(true);
    try {
      // Add to cart then go to checkout
      try { await api.post("/learner/cart/add-course", { course_id: course.id }); } catch (e) {
        // If already in cart or already owned, continue anyway
        if (!formatApiError(e).match(/already/i)) throw e;
      }
      nav("/cart");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  const addToCart = async (e) => {
    e.stopPropagation();
    if (!user) {
      const r = addToGuestCart(course);
      if (!r.added && r.reason === "duplicate") toast("This course is already in your cart.");
      else if (r.added) toast.success("Course added to cart successfully.");
      return;
    }
    try {
      await api.post("/learner/cart/add-course", { course_id: course.id });
      toast.success("Course added to cart successfully.");
      window.dispatchEvent(new CustomEvent("cart:updated"));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const badgeColor = (b) => {
    const map = {
      bestseller: "bg-[#329112] text-white border-transparent shadow-md",
      new: "bg-emerald-600 text-white border-transparent shadow-md",
      "government approved": "bg-blue-600 text-white border-transparent shadow-md",
      "limited seats": "bg-rose-600 text-white border-transparent shadow-md",
      "launching soon": "bg-purple-600 text-white border-transparent shadow-md",
      "live test": "bg-amber-500 text-white border-transparent shadow-md",
    };
    return map[(b || "").toLowerCase().trim()]
      || "bg-slate-800 text-white border-transparent shadow-md";
  };

  return (
    <div className="group flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white transition-all hover:shadow-xl hover:shadow-slate-200/60 hover:-translate-y-1"
         data-testid={`course-card-${course.slug}`}>
      <Link to={`/courses/${course.slug}`} className="relative overflow-hidden bg-slate-100">
        {course.thumbnail_url ? (
          <img src={resolveImg(course.thumbnail_url)} alt={course.course_title}
               className="h-48 w-full object-cover transition-transform duration-500 group-hover:scale-105"
               loading="lazy" />
        ) : (
          <div className="flex h-48 w-full items-center justify-center bg-gradient-to-br from-[#101876] to-[#329112]">
            <GraduationCap className="h-14 w-14 text-white/60" />
          </div>
        )}
        {course.course_badge && (
          <span className={`absolute left-3 top-3 rounded-lg px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide ${badgeColor(course.course_badge)}`}>
            {course.course_badge}
          </span>
        )}
        {course.discount_pct > 0 && (
          <span className="absolute right-3 top-3 rounded-lg bg-[#329112] px-2.5 py-1 text-[11px] font-bold text-white shadow-md">
            {course.discount_pct}% OFF
          </span>
        )}
      </Link>
      <div className="flex flex-1 flex-col p-4">
        <div className="mb-1.5 flex items-center gap-3 text-[11px] text-slate-500">
          <span className="inline-flex items-center gap-1"><Star className="h-3 w-3 text-amber-500 fill-amber-500" />{course.rating_placeholder || "4.7"}</span>
          <span className="inline-flex items-center gap-1"><Users className="h-3 w-3" />{(course.enrolled_count_placeholder || 0).toLocaleString()}</span>
          <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{course.duration_hours || 0}h</span>
        </div>
        <Link to={`/courses/${course.slug}`} className="hover:text-[#101876]">
          <h3 className="line-clamp-2 text-base font-bold text-slate-900 min-h-[3rem]">{course.course_title}</h3>
        </Link>
        {course.faculty_name && (
          <div className="mt-1 text-xs text-slate-500">By <span className="font-semibold text-slate-700">{course.faculty_name}</span></div>
        )}
        <div className="mt-3 flex items-baseline gap-2">
          {course.discount_price < course.base_price && (
            <span className="text-xs text-slate-400 line-through">{inr(course.base_price)}</span>
          )}
          <span className="text-xl font-black text-[#101876]">{inr(course.discount_price || course.base_price)}</span>
          {course.gst_applicable && !course.tax_inclusive && (
            <span className="text-[9px] uppercase tracking-widest text-slate-400">+GST</span>
          )}
        </div>
        <div className="mt-4 flex flex-1 items-end gap-2">
          {course.launching_soon ? (
            <button type="button" disabled className="flex-1 rounded-xl bg-slate-100 py-2.5 text-sm font-bold text-slate-500" data-testid={`btn-soon-${course.slug}`}>
              Launching Soon
            </button>
          ) : course.contact_only ? (
            <Link to={`/courses/${course.slug}#enquiry`} className="flex-1 rounded-xl bg-[#101876] py-2.5 text-center text-sm font-bold text-white hover:bg-[#0d1360]" data-testid={`btn-contact-${course.slug}`}>
              Contact Us
            </Link>
          ) : (
            <>
              {course.add_to_cart_enabled && (
                <button type="button" onClick={addToCart} data-testid={`btn-cart-${course.slug}`}
                        className="rounded-xl border-2 border-[#101876] px-3 py-2.5 text-sm font-bold text-[#101876] hover:bg-[#101876]/5 transition"
                        title="Add to cart">
                  <ShoppingCart className="h-4 w-4" />
                </button>
              )}
              {course.buy_now_enabled && (
                <button type="button" onClick={buyNow} disabled={busy} data-testid={`btn-buy-${course.slug}`}
                        className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-xl bg-[#329112] py-2.5 text-sm font-bold text-white hover:bg-[#26720d] transition disabled:opacity-70">
                  <Zap className="h-4 w-4" />{busy ? "..." : "Buy Now"}
                </button>
              )}
              <Link to={`/courses/${course.slug}`} data-testid={`btn-view-${course.slug}`}
                    className="rounded-xl border border-slate-200 px-3 py-2.5 text-sm font-bold text-slate-700 hover:bg-slate-50 transition">
                <ArrowRight className="h-4 w-4" />
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------
// FEATURED COURSES SECTION (drop-in for LandingPage)
// -----------------------------------------------------------
export function FeaturedCoursesSection() {
  const [courses, setCourses] = useState([]);
  useEffect(() => {
    publicFetch("/public/courses/featured?limit=8").then((d) => setCourses(d.courses || [])).catch(() => setCourses([]));
  }, []);
  if (!courses.length) return null;
  return (
    <section className="border-t border-slate-200 bg-white py-16 md:py-20" data-testid="featured-courses">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="mb-8 flex items-end justify-between gap-3">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#329112]">Popular now</div>
            <h2 className="mt-2 text-2xl font-black text-slate-900 sm:text-3xl md:text-4xl" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
              Featured Healthcare Courses
            </h2>
            <p className="mt-1 max-w-2xl text-sm text-slate-600">
              Curated certifications and compliance programs — start learning today with 40–60% launch discounts.
            </p>
          </div>
          <Link to="/courses" className="hidden sm:inline-flex items-center gap-1 text-sm font-bold text-[#101876] hover:text-[#329112]" data-testid="view-all-courses">
            View all courses <ChevronRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {courses.map((c) => <CourseCard key={c.id} course={c} />)}
        </div>
      </div>
    </section>
  );
}

// -----------------------------------------------------------
// PUBLIC COURSES LISTING PAGE (/courses)
// -----------------------------------------------------------
export function PublicCoursesPage() {
  usePageTitle("All Courses");
  const [courses, setCourses] = useState(null);
  const [q, setQ] = useState("");
  const load = async (query = "") => {
    try {
      const d = query
        ? await publicFetch(`/public/courses/search?q=${encodeURIComponent(query)}`)
        : await publicFetch("/public/courses?limit=48");
      setCourses(d.courses || []);
    } catch (err) { toast.error(err.message); setCourses([]); }
  };
  useEffect(() => { load(""); }, []);
  return (
    <div className="min-h-screen bg-slate-50">
      <StoreHeader />
      <div className="mx-auto max-w-7xl px-4 py-10 md:px-6">
        <div className="mb-6 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <h1 className="text-3xl font-black text-slate-900">All Courses</h1>
          <div className="flex items-center gap-2">
            <input type="search" value={q} onChange={(e) => setQ(e.target.value)}
                   onKeyDown={(e) => e.key === "Enter" && load(q)}
                   placeholder="Search NABH, BLS, IPC..." data-testid="courses-search"
                   className="w-full md:w-80 rounded-xl border border-slate-300 px-4 py-2.5 text-sm focus:border-[#101876] focus:outline-none focus:ring-2 focus:ring-[#101876]/20" />
            <button type="button" onClick={() => load(q)} className="rounded-xl bg-[#101876] px-5 py-2.5 text-sm font-bold text-white hover:bg-[#0d1360]">Search</button>
          </div>
        </div>
        {courses === null ? <div className="text-center py-20 text-slate-500">Loading...</div> :
         courses.length === 0 ? <div className="text-center py-20 text-slate-500">No courses match your search.</div> :
         <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="courses-grid">
           {courses.map((c) => <CourseCard key={c.id} course={c} />)}
         </div>}
      </div>
    </div>
  );
}

// -----------------------------------------------------------
// COURSE DETAIL PAGE (/courses/:slug) — Graphy-style with sticky Buy Now
// -----------------------------------------------------------
export function CourseDetailPage() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const waUrl = useWhatsAppUrl();
  const [c, setC] = useState(null);
  const [enrolled, setEnrolled] = useState(false);
  const [busy, setBusy] = useState(false);
  const [emiAvailable, setEmiAvailable] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const d = await publicFetch(`/public/courses/${slug}`);
        setC(d);
        document.title = `${d.course_title} · Steward Learning Cloud`;
        if (user) {
          try {
            const r = await api.get(`/learner/course-access/${d.id}`);
            setEnrolled(r.data?.data?.enrolled || false);
          } catch { /* ignore */ }
        }
        // Public: check if any active installment plan exists for this course
        try {
          const r2 = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/installment-options/${d.id}`);
          if (r2.ok) {
            const j = await r2.json();
            setEmiAvailable((j.data?.installment_plans || []).length > 0);
          }
        } catch { /* silent */ }
      } catch (err) { toast.error(err.message); nav("/courses"); }
    })();
    // eslint-disable-next-line
  }, [slug, user?.id]);

  const buyNow = async () => {
    if (!c || c.launching_soon) return;
    if (c.contact_only) return document.getElementById("enquiry")?.scrollIntoView({ behavior: "smooth" });
    if (!user) {
      const r = addToGuestCart(c);
      if (!r.added && r.reason === "duplicate") toast("This course is already in your cart.");
      else if (r.added) toast.success("Course added to cart.");
      setCheckoutRedirect(true);
      return nav("/cart");
    }
    setBusy(true);
    try {
      try { await api.post("/learner/cart/add-course", { course_id: c.id }); } catch (e) {
        if (!formatApiError(e).match(/already/i)) throw e;
      }
      nav("/cart");
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };

  const addToCart = async () => {
    if (!c) return;
    if (!user) {
      const r = addToGuestCart(c);
      if (!r.added && r.reason === "duplicate") toast("This course is already in your cart.");
      else if (r.added) toast.success("Course added to cart successfully.");
      return;
    }
    try {
      await api.post("/learner/cart/add-course", { course_id: c.id });
      toast.success("Course added to cart successfully.");
      window.dispatchEvent(new CustomEvent("cart:updated"));
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (!c) return <div className="min-h-screen bg-slate-50"><StoreHeader /><div className="p-20 text-center text-slate-500">Loading...</div></div>;

  const priceCard = (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="detail-price-card">
      {(() => {
        // Sidebar always prefers the uploaded course thumbnail. The
        // full YouTube/mp4 preview lives in the main "Preview this course"
        // section further down the page.
        if (c.thumbnail_url) {
          return (
            <img src={resolveImg(c.thumbnail_url)} alt={c.course_title} className="mb-4 aspect-video w-full rounded-xl object-cover" />
          );
        }
        const url = c.preview_video_url || "";
        const isYouTube = /(youtu\.be\/|youtube\.com\/(watch\?v=|embed\/|shorts\/))/i.test(url);
        if (url && isYouTube) {
          const m = url.match(/(?:youtu\.be\/|v=|embed\/|shorts\/)([\w-]{6,})/);
          const vid = m ? m[1] : "";
          if (vid) {
            return (
              <img
                src={`https://img.youtube.com/vi/${vid}/hqdefault.jpg`}
                alt={c.course_title}
                className="mb-4 aspect-video w-full rounded-xl object-cover"
              />
            );
          }
        }
        return null;
      })()}
      <div className="flex items-baseline gap-2">
        <span className="text-3xl font-black text-[#101876]">{inr(c.discount_price || c.base_price)}</span>
        {c.discount_price < c.base_price && (
          <>
            <span className="text-sm text-slate-400 line-through">{inr(c.base_price)}</span>
            <span className="rounded bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-800">{c.discount_pct}% OFF</span>
          </>
        )}
      </div>
      {c.gst_applicable && !c.tax_inclusive && <div className="text-[10px] uppercase tracking-widest text-slate-400 mt-0.5">+ GST</div>}
      <div className="mt-4 space-y-2" data-testid="detail-buttons">
        {enrolled ? (
          <button type="button" onClick={() => nav("/app/student/courses")} className="w-full rounded-xl bg-[#101876] py-3 text-sm font-bold text-white hover:bg-[#0d1360]" data-testid="btn-go-to-course">
            <PlayCircle className="inline h-4 w-4 mr-2" />Go to Course
          </button>
        ) : c.launching_soon ? (
          <button type="button" disabled className="w-full rounded-xl bg-slate-100 py-3 text-sm font-bold text-slate-500">Launching Soon</button>
        ) : c.contact_only ? (
          <a href="#enquiry" className="block w-full rounded-xl bg-[#101876] py-3 text-center text-sm font-bold text-white hover:bg-[#0d1360]">Contact for Details</a>
        ) : (
          <>
            <button type="button" onClick={buyNow} disabled={busy} data-testid="btn-buy-detail"
                    className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[#329112] py-3 text-sm font-bold text-white hover:bg-[#26720d] transition disabled:opacity-70">
              <Zap className="h-4 w-4" />{busy ? "Processing…" : "Buy Now"}
            </button>
            {c.add_to_cart_enabled && (
              <button type="button" onClick={addToCart} data-testid="btn-cart-detail"
                      className="w-full inline-flex items-center justify-center gap-2 rounded-xl border-2 border-[#101876] py-3 text-sm font-bold text-[#101876] hover:bg-[#101876]/5">
                <ShoppingCart className="h-4 w-4" />Add to Cart
              </button>
            )}
            {emiAvailable && (
              <button type="button"
                onClick={() => user ? nav(`/checkout/installment/${c.id}`) : nav(`/login?next=/checkout/installment/${c.id}`)}
                data-testid="btn-emi-detail"
                className="w-full inline-flex items-center justify-center gap-2 rounded-xl border-2 border-emerald-500 bg-white py-3 text-sm font-bold text-emerald-700 hover:bg-emerald-50">
                <Zap className="h-4 w-4" />Pay in Installments
              </button>
            )}
            <a href={c.course_title ? `${waUrl}${waUrl.includes("?") ? "&" : "?"}text=${encodeURIComponent("Hi, I'm interested in " + c.course_title)}` : waUrl}
               target="_blank" rel="noreferrer" data-testid="btn-wa-enquiry"
               className="block text-center w-full rounded-xl border border-emerald-200 bg-emerald-50 py-2.5 text-sm font-bold text-emerald-800 hover:bg-emerald-100">
              <MessageCircle className="inline h-4 w-4 mr-1" />WhatsApp Enquiry
            </a>
            {c.brochure_url && (
              <a href={resolveImg(c.brochure_url)} target="_blank" rel="noreferrer"
                 download data-testid="btn-download-brochure"
                 className="block text-center w-full rounded-xl border-2 border-[#101876] bg-white py-2.5 text-sm font-bold text-[#101876] hover:bg-[#101876]/5">
                <Download className="inline h-4 w-4 mr-1.5" />Download Brochure
              </a>
            )}
          </>
        )}
      </div>
      <div className="mt-4 space-y-1.5 text-xs text-slate-600 border-t border-slate-100 pt-3">
        <div className="flex items-center gap-2"><Clock className="h-3.5 w-3.5" />{c.duration_hours}h of content</div>
        <div className="flex items-center gap-2"><GraduationCap className="h-3.5 w-3.5" />{c.total_modules || 0} modules · {c.total_lessons || 0} lessons</div>
        <div className="flex items-center gap-2"><Award className="h-3.5 w-3.5" />Certificate of completion</div>
        <div className="flex items-center gap-2"><Users className="h-3.5 w-3.5" />{(c.enrolled_count_placeholder || 0).toLocaleString()} enrolled</div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 pb-24 lg:pb-6">
      <StoreHeader />
      {/* Hero banner */}
      <div className="border-b border-slate-200 bg-gradient-to-b from-[#101876] to-[#0d1360] text-white">
        <div className="mx-auto max-w-7xl px-4 py-10 md:px-6 md:py-16">
          <div className="text-xs uppercase tracking-[0.2em] text-[#7ce561]">{c.category_name || c.course_type}</div>
          <h1 className="mt-3 text-3xl font-black md:text-4xl lg:text-5xl" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>{c.course_title}</h1>
          <p className="mt-3 max-w-3xl text-sm md:text-base opacity-80">{c.short_description}</p>
          <div className="mt-5 flex flex-wrap items-center gap-4 text-xs">
            <span className="inline-flex items-center gap-1"><Star className="h-4 w-4 fill-amber-400 text-amber-400" /> {c.rating_placeholder || 4.7} rating</span>
            <span className="inline-flex items-center gap-1"><Users className="h-4 w-4" /> {(c.enrolled_count_placeholder || 0).toLocaleString()} learners</span>
            <span className="inline-flex items-center gap-1"><Clock className="h-4 w-4" /> {c.duration_hours}h</span>
            <span className="inline-flex items-center gap-1 rounded bg-white/10 px-2 py-0.5">{c.course_type}</span>
            <span className="inline-flex items-center gap-1 rounded bg-white/10 px-2 py-0.5">{c.level}</span>
            {c.faculty_name && <span className="inline-flex items-center gap-1">By <b>{c.faculty_name}</b></span>}
          </div>
        </div>
      </div>
      <div className="mx-auto grid max-w-7xl gap-8 px-4 py-10 md:px-6 lg:grid-cols-[1fr,360px]">
        {/* Left: content */}
        <div className="space-y-8" data-testid="detail-content">
          {/* Mobile-only inline purchase card — desktop uses the sticky sidebar on the right */}
          <div className="lg:hidden" data-testid="detail-price-card-mobile">
            {priceCard}
          </div>
          {(() => {
            const outcomes = Array.isArray(c.learning_outcomes)
              ? c.learning_outcomes
              : (typeof c.learning_outcomes === "string"
                  ? c.learning_outcomes.split(/[\r\n]+/).map((s) => s.replace(/^[\s\-\u2022]+/, "").trim()).filter(Boolean)
                  : []);
            return outcomes.length > 0 && (
            <section className="rounded-2xl border border-slate-200 bg-white p-6">
              <h2 className="text-lg font-black text-slate-900">What you will learn</h2>
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                {outcomes.map((o, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm text-slate-700">
                    <CheckCircle2 className="h-4 w-4 text-[#329112] mt-0.5 shrink-0" /> {o}
                  </div>
                ))}
              </div>
            </section>
            );
          })()}
          <section className="rounded-2xl border border-slate-200 bg-white p-6">
            <h2 className="text-lg font-black text-slate-900">About this course</h2>
            {c.description ? (
              <p className="mt-3 text-sm text-slate-700 whitespace-pre-line leading-relaxed">{c.description}</p>
            ) : (
              <p className="mt-3 text-sm text-slate-500 italic leading-relaxed">
                Course description not added yet. An administrator can add it from
                <b> Admin → Courses → Edit → Description</b>.
              </p>
            )}
          </section>

          {/* Preview YouTube video — appears when the admin has set
              `preview_video_url` on the course (Admin → Courses → Edit →
              Media). Supports both direct video URLs (mp4/webm) and
              YouTube share URLs (youtu.be / youtube.com/watch?v=). */}
          {(() => {
            const url = c.preview_video_url || "";
            const isYouTube = /(youtu\.be\/|youtube\.com\/(watch\?v=|embed\/|shorts\/))/i.test(url);
            const isDirectVideo = /\.(mp4|webm|ogg|mov)(\?|$)/i.test(url);
            if (!isYouTube && !isDirectVideo) return null;
            return (
            <section className="rounded-2xl border border-slate-200 bg-white p-6">
              <h2 className="text-lg font-black text-slate-900">Preview this course</h2>
              <p className="mt-1 text-xs text-slate-500">Watch a short intro before enrolling.</p>
              <div className="mt-4 aspect-video w-full overflow-hidden rounded-xl bg-black">
                {(() => {
                  if (isYouTube) {
                    const m = url.match(/(?:youtu\.be\/|v=|embed\/|shorts\/)([\w-]{6,})/);
                    const vid = m ? m[1] : "";
                    if (!vid) return null;
                    return (
                      <iframe
                        src={`https://www.youtube.com/embed/${vid}?rel=0&modestbranding=1`}
                        title="Course preview"
                        className="h-full w-full"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        data-testid="course-preview-youtube"
                      />
                    );
                  }
                  return <video src={url} controls className="h-full w-full" data-testid="course-preview-video" />;
                })()}
              </div>
            </section>
            );
          })()}
          {c.modules?.length > 0 && (
            <section className="rounded-2xl border border-slate-200 bg-white p-6">
              <h2 className="text-lg font-black text-slate-900">Course content</h2>
              <div className="mt-3 divide-y divide-slate-100">
                {c.modules.map((m, i) => (
                  <div key={m.id} className="py-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#101876]/10 text-xs font-bold text-[#101876]">{i + 1}</div>
                      <div className="text-sm font-semibold text-slate-800">{m.title}</div>
                    </div>
                    <div className="text-xs text-slate-500">{m.lesson_count} lessons</div>
                  </div>
                ))}
              </div>
            </section>
          )}
          {c.contact_only && (
            <section id="enquiry" className="rounded-2xl border border-slate-200 bg-white p-6">
              <h2 className="text-lg font-black text-slate-900">Enquire about this course</h2>
              <p className="mt-1 text-sm text-slate-600">Our team will reach out with pricing and enrollment details.</p>
              <a href={c.course_title ? `${waUrl}${waUrl.includes("?") ? "&" : "?"}text=${encodeURIComponent("Hi, I'm interested in " + c.course_title)}` : waUrl}
                 target="_blank" rel="noreferrer"
                 className="mt-4 inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-emerald-700">
                <MessageCircle className="h-4 w-4" />Chat on WhatsApp
              </a>
            </section>
          )}
        </div>
        {/* Right: sticky purchase card */}
        <aside className="hidden lg:block">
          <div className="sticky top-24">{priceCard}</div>
        </aside>
        {/* Mobile: bottom sticky bar */}
        <div className="fixed bottom-0 left-0 right-0 z-30 border-t border-slate-200 bg-white px-4 py-3 shadow-2xl shadow-slate-900/10 lg:hidden">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-lg font-black text-[#101876]">{inr(c.discount_price || c.base_price)}</div>
              {c.discount_price < c.base_price && <div className="text-[10px] text-slate-400 line-through">{inr(c.base_price)}</div>}
            </div>
            {enrolled ? (
              <button type="button" onClick={() => nav("/app/student/courses")} className="flex-1 rounded-xl bg-[#101876] py-3 text-sm font-bold text-white">Go to Course</button>
            ) : c.launching_soon ? (
              <button type="button" disabled className="flex-1 rounded-xl bg-slate-100 py-3 text-sm font-bold text-slate-500">Launching Soon</button>
            ) : (
              <button type="button" onClick={buyNow} disabled={busy} className="flex-1 rounded-xl bg-[#329112] py-3 text-sm font-bold text-white">
                {busy ? "…" : "Buy Now"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------
// STORE HEADER (reused across storefront pages) — with cart icon + auth
// -----------------------------------------------------------
export function StoreHeader() {
  const [cartCount, setCartCount] = useState(0);
  const { user, logout } = useAuth();
  const { pages } = useSitePageVisibility();
  // Show in header ONLY when the page is both active AND flagged for the
  // header. `show_in_header=false && page_active=true` means the page is
  // "unlisted": accessible via direct URL but not surfaced in the nav.
  const isOn = (k) => {
    const v = pages[k];
    if (v === undefined) return true; // fail-open
    if (typeof v === "boolean") return v;
    return !!(v.show_in_header && v.page_active);
  };
  const nav = useNavigate();

  const refresh = async () => {
    if (!user) return setCartCount(getGuestCartCount());
    try {
      const { data } = await api.get("/learner/cart");
      setCartCount(data?.data?.items?.length || 0);
    } catch { /* ignore */ }
  };
  useEffect(() => {
    refresh();
    const on = () => refresh();
    window.addEventListener("cart:updated", on);
    window.addEventListener("storage", on);  // multi-tab sync
    return () => {
      window.removeEventListener("cart:updated", on);
      window.removeEventListener("storage", on);
    };
    // eslint-disable-next-line
  }, [user?.id]);

  return (
    <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/85 backdrop-blur">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 md:px-6">
        <Link to="/"><Logo variant="compact" showText={false} /></Link>
        <nav className="hidden md:flex items-center gap-6 text-sm font-semibold text-slate-700">
          {isOn("courses") && <Link to="/courses" className="hover:text-[#101876]" data-testid="nav-courses">Courses</Link>}
          {isOn("section_why") && <Link to="/#why" className="hover:text-[#101876]" data-testid="nav-why">Why Steward</Link>}
          {isOn("section_training") && <Link to="/#categories" className="hover:text-[#101876]" data-testid="nav-training">Training</Link>}
          {isOn("for_hospitals") && <Link to="/for-hospitals" className="hover:text-[#101876]" data-testid="nav-hospitals">For Hospitals</Link>}
          {isOn("section_faq") && <Link to="/#faq" className="hover:text-[#101876]" data-testid="nav-faq">FAQ</Link>}
          {isOn("section_contact") && <Link to="/#contact" className="hover:text-[#101876]" data-testid="nav-contact">Contact</Link>}
          {isOn("verify_certificate") && <Link to="/verify-certificate" className="hover:text-[#101876]" data-testid="nav-verify">Verify Certificate</Link>}
        </nav>
        <div className="flex items-center gap-3">
          <Link to="/cart" data-testid="nav-cart" className="relative rounded-full border border-slate-200 p-2 hover:bg-slate-50">
            <ShoppingCart className="h-4 w-4 text-slate-700" />
            {cartCount > 0 && (
              <span className="absolute -top-1 -right-1 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[#329112] px-1 text-[10px] font-bold text-white">
                {cartCount}
              </span>
            )}
          </Link>
          {user ? (
            <div className="flex items-center gap-2">
              <Link to="/app" className="rounded-xl bg-[#101876] px-4 py-2 text-xs font-bold text-white hover:bg-[#0d1360]">Dashboard</Link>
              <button type="button" onClick={() => { logout(); nav("/"); }} className="text-xs text-slate-500 hover:text-slate-800">Sign out</button>
            </div>
          ) : (
            <>
              <Link to="/login" className="text-xs font-bold text-slate-700 hover:text-[#101876]">Log in</Link>
              <Link to="/signup" className="rounded-xl bg-[#101876] px-4 py-2 text-xs font-bold text-white hover:bg-[#0d1360]">Sign up</Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}

// -----------------------------------------------------------
// PUBLIC CART PAGE (/cart) — for both authed + guest (guest redirected)
// -----------------------------------------------------------
export function PublicCartPage() {
  usePageTitle("My Cart");
  const { user } = useAuth();
  const nav = useNavigate();
  const [cart, setCart] = useState(null);
  const [coupon, setCoupon] = useState("");
  const [discount, setDiscount] = useState(null);
  const [availableCoupons, setAvailableCoupons] = useState([]);
  const [billingOpen, setBillingOpen] = useState(false);
  // Course-wise EMI selection state. `emiPlans[course_id]` → list of
  // active installment plans for that course; `emiMode` is the
  // course_id currently toggled for EMI checkout (null = full-pay
  // cart). `emiPlanId` picks a specific plan when a course has
  // multiple plans (we pre-select the first one).
  const [emiPlans, setEmiPlans] = useState({});
  const [emiMode, setEmiMode] = useState(null);
  const [emiPlanId, setEmiPlanId] = useState("");
  // Signup fields — only used when the shopper isn't logged in and picks the
  // guest checkout path. Kept separate from billing so their profile name
  // (first/last) can differ from the billing name on the invoice.
  const [signup, setSignup] = useState({ first_name: "", last_name: "", password: "", confirm_password: "" });
  const [billing, setBilling] = useState({
    // Start empty by default. Personal details (name/email/mobile/address)
    // must NEVER carry over from a previous shopper on the same browser —
    // that produced invoices with the wrong buyer name. The "Pre-fill from
    // user profile" effect below rehydrates fields when the shopper is
    // signed in.
    billing_name: "", billing_email: "", billing_mobile: "",
    billing_address: "", billing_city: "", billing_state: "",
    billing_country: "India", billing_pincode: "", billing_gst_number: "",
  });

  const loadGuestAsCart = () => {
    const guestItems = getGuestCart().map((g) => ({
      id: `guest-${g.course_id}`,
      course_id: g.course_id,
      // Nest under `course` so the same JSX (it.course?.course_title etc.) works
      // for both guest (localStorage) and authenticated (backend) carts.
      course: {
        course_title: g.course_title,
        course_code: g.slug || "",
        thumbnail_url: g.thumbnail_url,
      },
      // Keep flat fields too for backwards compatibility with any helper reads.
      course_title: g.course_title,
      thumbnail_url: g.thumbnail_url,
      slug: g.slug,
      base_price: g.base_price,
      discount_price: g.discount_price,
      final_amount: g.discount_price || g.base_price,
    }));
    setCart({ items: guestItems, guest: true });
  };

  useEffect(() => {
    if (!user) {
      loadGuestAsCart();
      // Rehydrate saved coupon
      const c = getAppliedCoupon();
      if (c) setCoupon(c);
      // Fetch homepage-visible coupons as guest-viewable list
      (async () => {
        try {
          const { data } = await api.get("/public/coupons/homepage");
          setAvailableCoupons(data.data?.coupons || []);
        } catch { /* silent */ }
      })();
      return;
    }
    (async () => {
      try {
        const { data } = await api.get("/learner/cart");
        setCart(data.data);
        // Fetch server-side eligible coupons
        try {
          const r = await api.get("/learner/cart/available-coupons");
          setAvailableCoupons(r.data.data?.coupons || []);
        } catch { /* silent */ }
        // Re-apply saved guest coupon (if any) against the merged authed cart
        const c = getAppliedCoupon();
        if (c && data.data.items?.length) {
          setCoupon(c);
          try {
            const r = await api.post("/learner/checkout/apply-coupon", {
              coupon_code: c,
              course_ids: data.data.items.map((i) => i.course_id),
              order_amount: data.data.items.reduce((s, i) => s + (i.final_amount || 0), 0),
            });
            setDiscount(r.data.data);
          } catch {
            toast("This coupon is not valid for your account or selected course.");
            setAppliedCoupon("");
          }
        }
      } catch (err) { toast.error(formatApiError(err)); setCart({ items: [] }); }
    })();
    // eslint-disable-next-line
  }, [user?.id]);

  const applyFromList = async (code) => {
    setCoupon(code);
    // Give React a beat so applyCoupon reads the new value
    setTimeout(() => applyCoupon(), 50);
  };

  const remove = async (item) => {
    if (!user) {
      removeFromGuestCart(item.course_id);
      loadGuestAsCart();
      toast.success("Removed");
      return;
    }
    try {
      await api.delete(`/learner/cart/items/${item.id}`);
      toast.success("Removed");
      const { data } = await api.get("/learner/cart"); setCart(data.data);
      window.dispatchEvent(new CustomEvent("cart:updated"));
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const applyCoupon = async () => {
    if (!coupon.trim() || !cart?.items?.length) return;
    if (!user) {
      // GUEST: preview coupon via public endpoint so we can show the final amount now.
      try {
        const { data } = await api.post("/public/coupons/apply", {
          coupon_code: coupon.trim().toUpperCase(),
          course_ids: cart.items.map((i) => i.course_id),
          order_amount: subtotal,
        });
        setDiscount({ discount_amount: data.data.discount_amount });
        setAppliedCoupon(coupon.trim().toUpperCase());
        toast.success(`Coupon applied — you save ${inr(data.data.discount_amount)}`);
      } catch (err) {
        setDiscount(null);
        toast.error(formatApiError(err));
      }
      return;
    }
    try {
      const { data } = await api.post("/learner/checkout/apply-coupon", {
        coupon_code: coupon.trim().toUpperCase(),
        course_ids: cart.items.map((i) => i.course_id),
        order_amount: subtotal,
      });
      setDiscount(data.data);
      setAppliedCoupon(coupon.trim().toUpperCase());
      toast.success(`Coupon applied — you save ${inr(data.data.discount_amount)}`);
    } catch (err) { toast.error(formatApiError(err)); setDiscount(null); }
  };

  const subtotal = useMemo(
    () => (cart?.items || []).reduce((s, i) => s + (i.final_amount || 0), 0),
    [cart]
  );

  // Course-wise EMI plans — fetch once per cart item set so the toggle
  // can appear on each row that has an active plan configured by Super
  // Admin. Public endpoint so this works for guests + logged-in
  // shoppers.
  useEffect(() => {
    const ids = (cart?.items || []).map((i) => i.course_id).filter(Boolean);
    if (!ids.length) { setEmiPlans({}); return; }
    let cancelled = false;
    (async () => {
      const map = {};
      await Promise.all(ids.map(async (cid) => {
        try {
          const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/installment-options/${cid}`);
          const d = await r.json();
          const plans = d.data?.installment_plans || [];
          if (plans.length) map[cid] = plans;
        } catch { /* silent */ }
      }));
      if (!cancelled) setEmiPlans(map);
    })();
    return () => { cancelled = true; };
  }, [cart?.items?.map((i) => i.course_id).join(",")]); // eslint-disable-line

  // Derived EMI numbers — reused across the Order Summary and the
  // billing modal footer.
  const emiPlan = useMemo(() => {
    if (!emiMode) return null;
    const plans = emiPlans[emiMode] || [];
    return plans.find((p) => p.id === emiPlanId) || plans[0] || null;
  }, [emiMode, emiPlanId, emiPlans]);
  const emiCourse = useMemo(
    () => (cart?.items || []).find((i) => i.course_id === emiMode) || null,
    [cart, emiMode],
  );
  // When a coupon is applied in EMI mode we shave it off the LAST
  // installment (never the down payment). This is purely a UI preview;
  // the backend applies the same math on `/public/checkout/guest-installment`.
  const emiLastInstallmentAfterCoupon = useMemo(() => {
    if (!emiPlan) return null;
    const d = emiMode && discount?.discount_amount ? discount.discount_amount : 0;
    return Math.max(0, (emiPlan.installment_amount || 0) - d);
  }, [emiPlan, discount, emiMode]);

  const toggleEmi = (courseId) => {
    if (emiMode === courseId) { setEmiMode(null); setEmiPlanId(""); return; }
    const plans = emiPlans[courseId] || [];
    if (!plans.length) return;
    setEmiMode(courseId);
    setEmiPlanId(plans[0].id);
  };
  // Live preview of the exact total the backend will charge (incl. GST when
  // any item has gst_applicable). We refetch whenever cart, coupon or
  // billing state change so the shopper never sees a surprise on Razorpay.
  const [preview, setPreview] = useState(null);
  useEffect(() => {
    const cids = (cart?.items || []).map((i) => i.course_id).filter(Boolean);
    if (!cids.length) { setPreview(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post("/public/checkout/preview", {
          course_ids: cids,
          coupon_code: (discount ? coupon : "") || "",
          billing_state: billing.billing_state || "",
        });
        if (!cancelled) setPreview(data.data);
      } catch { if (!cancelled) setPreview(null); }
    })();
    return () => { cancelled = true; };
  }, [cart?.items, discount, coupon, billing.billing_state]);

  const total = preview
    ? preview.total
    : Math.max(0, subtotal - (discount?.discount_amount || 0));
  const previewGst = preview?.tax_amount || 0;

  const checkout = async () => {
    if (!cart?.items?.length) return;
    // Pre-fill from user profile if the billing form is still blank so the
    // logged-in shopper doesn't have to retype what we already know.
    if (user) {
      setBilling((b) => ({
        ...b,
        billing_name: b.billing_name || user.full_name || `${user.first_name || ""} ${user.last_name || ""}`.trim(),
        billing_email: b.billing_email || user.email || "",
        billing_mobile: b.billing_mobile || user.mobile || "",
      }));
    }
    setBillingOpen(true);
  };

  const submitCheckout = async () => {
    // Guest path — require signup fields alongside billing so we create the
    // account + order + payment intent in a single request. No login round-trip.
    if (!user) {
      const missing = [];
      if (!signup.first_name.trim()) missing.push("First name");
      if (!signup.last_name.trim()) missing.push("Last name");
      if (!signup.password || signup.password.length < 8) missing.push("Password (min 8 chars)");
      if (signup.password !== signup.confirm_password) missing.push("Confirm password (must match)");
      if (!billing.billing_email.trim()) missing.push("Email");
      if (!billing.billing_mobile.trim()) missing.push("Mobile");
      if (!billing.billing_address.trim()) missing.push("Address");
      if (!billing.billing_city.trim()) missing.push("City");
      if (!billing.billing_state.trim()) missing.push("State");
      if (!billing.billing_pincode.trim()) missing.push("Pincode");
      if (missing.length) { toast.error(`Please fill: ${missing.join(", ")}`); return; }
      try {
        try { localStorage.removeItem("slc_billing"); } catch { /* ignore */ }
        if (emiMode && emiPlan) {
          // EMI path — guest signup + down-payment order via dedicated endpoint
          const { data } = await api.post("/public/checkout/guest-installment", {
            first_name: signup.first_name.trim(),
            last_name: signup.last_name.trim(),
            password: signup.password,
            course_id: emiMode,
            plan_id: emiPlan.id,
            terms_accepted: true,
            coupon_code: discount ? coupon.trim().toUpperCase() : "",
            ...billing,
            billing_name: billing.billing_name || `${signup.first_name.trim()} ${signup.last_name.trim()}`,
          });
          const order = data.data;
          setBillingOpen(false);
          clearGuestCart();
          toast.success("Account banaya — down payment ke liye payment start kar rahe hain…");
          await launchRazorpay(order, {
            name: billing.billing_name || `${signup.first_name} ${signup.last_name}`,
            email: billing.billing_email,
            contact: billing.billing_mobile,
          });
          return;
        }
        const { data } = await api.post("/public/checkout/guest", {
          first_name: signup.first_name.trim(),
          last_name: signup.last_name.trim(),
          password: signup.password,
          course_ids: cart.items.map((i) => i.course_id),
          coupon_code: discount ? coupon.trim().toUpperCase() : "",
          ...billing,
          billing_name: billing.billing_name || `${signup.first_name.trim()} ${signup.last_name.trim()}`,
        });
        const order = data.data;
        setBillingOpen(false);
        clearGuestCart();
        toast.success("Account created — launching payment…");
        await launchRazorpay(order, {
          name: billing.billing_name || `${signup.first_name} ${signup.last_name}`,
          email: billing.billing_email,
          contact: billing.billing_mobile,
        });
      } catch (err) { toast.error(formatApiError(err)); }
      return;
    }

    // Authenticated path — regular checkout.
    const required = [
      ["billing_name", "Full name"],
      ["billing_email", "Email"],
      ["billing_mobile", "Mobile"],
      ["billing_address", "Address"],
      ["billing_city", "City"],
      ["billing_state", "State"],
      ["billing_pincode", "Pincode"],
    ];
    const missing = required.filter(([k]) => !(billing[k] || "").toString().trim());
    if (missing.length) {
      toast.error(`Please fill: ${missing.map((m) => m[1]).join(", ")}`);
      return;
    }
    try { localStorage.setItem("slc_billing", JSON.stringify(billing)); } catch { /* ignore */ }
    try {
      if (emiMode && emiPlan) {
        // Authenticated EMI — reuse existing endpoint; backend applies
        // the coupon to the last installment via its own path.
        const { data } = await api.post("/learner/checkout/select-installment-plan", {
          plan_id: emiPlan.id,
          course_id: emiMode,
          terms_accepted: true,
          coupon_code: discount ? coupon.trim().toUpperCase() : "",
          ...billing,
        });
        const order = data.data;
        // Optional coupon → notify backend via dedicated endpoint later.
        setBillingOpen(false);
        await launchRazorpay(order, {
          name: billing.billing_name, email: billing.billing_email, contact: billing.billing_mobile,
        });
        return;
      }
      const { data } = await api.post("/learner/checkout/create-order", {
        ...billing,
        coupon_code: discount ? coupon.trim().toUpperCase() : "",
      });
      const order = data.data;
      setBillingOpen(false);
      await launchRazorpay(order, {
        name: billing.billing_name, email: billing.billing_email, contact: billing.billing_mobile,
      });
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const launchRazorpay = async (order, prefill) => {
    const keyId = order.razorpay_key_id || "";
    const hasRealKey = keyId && !keyId.includes("placeholder");
    if (window.Razorpay && hasRealKey) {
      const rzp = new window.Razorpay({
        key: keyId,
        amount: order.amount_paise, currency: order.currency || "INR",
        order_id: order.razorpay_order_id,
        name: "Steward Learning Cloud",
        description: `${cart.items.length} course(s)`,
        handler: async (resp) => {
          try {
            const { data: v } = await api.post("/learner/checkout/verify-payment", {
              order_id: order.order?.id || order.order_id,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            const d = v?.data || {};
            const qs = new URLSearchParams({
              invoice_id: d.invoice_id || "",
              invoice_number: d.invoice_number || "",
              order_number: d.order_number || "",
              total: (d.total_amount ?? "").toString(),
              email: d.billing_email || "",
            }).toString();
            toast.success("Payment successful!");
            nav(`/thank-you?${qs}`);
          } catch (err) { toast.error(formatApiError(err)); }
        },
        prefill,
        theme: { color: "#101876" },
      });
      rzp.open();
      return;
    }
    // No real key was returned by the backend.
    if (order.test_mode) {
      // Backend is in test mode too — use the simulated payment path.
      await api.post(`/learner/checkout/test-pay/${order.order?.id || order.order_id}`);
      toast.success("Payment simulated (test mode)");
      nav("/app/student/courses?paid=1");
      return;
    }
    // Live mode + no key configured — surface a clear message so the admin
    // knows to configure Razorpay under Admin → Payment Gateways.
    toast.error("Payment gateway is not configured yet. Please contact support (info@stewardindia.com) to complete this order.");
  };

  if (!cart) return <div className="min-h-screen bg-slate-50"><StoreHeader /><div className="p-20 text-center text-slate-500">Loading...</div></div>;
  return (
    <div className="min-h-screen bg-slate-50">
      <StoreHeader />
      <div className="mx-auto max-w-6xl px-4 py-10 md:px-6" data-testid="cart-page">
        <h1 className="mb-6 text-3xl font-black text-slate-900">My Cart</h1>
        {(cart.items || []).length === 0 ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-16 text-center">
            <ShoppingCart className="mx-auto h-10 w-10 text-slate-300" />
            <div className="mt-3 text-lg font-bold text-slate-800">Your cart is empty</div>
            <Link to="/courses" className="mt-4 inline-block rounded-xl bg-[#101876] px-5 py-2.5 text-sm font-bold text-white">Browse courses</Link>
          </div>
        ) : (
          <div className="grid gap-6 lg:grid-cols-[1fr,340px]">
            <div className="space-y-3">
              {cart.items.map((it) => {
                const plans = emiPlans[it.course_id] || [];
                const isEmi = emiMode === it.course_id;
                const dimmed = emiMode && emiMode !== it.course_id;
                const activePlan = isEmi ? (plans.find((p) => p.id === emiPlanId) || plans[0]) : plans[0];
                return (
                <div key={it.id} className={`rounded-2xl border bg-white p-4 transition ${isEmi ? "border-emerald-300 shadow-sm ring-2 ring-emerald-100" : "border-slate-200"} ${dimmed ? "opacity-50" : ""}`} data-testid={`cart-item-${it.id}`}>
                  <div className="flex gap-4">
                    {it.course?.thumbnail_url && <img src={resolveImg(it.course.thumbnail_url)} className="h-20 w-32 rounded-lg object-cover" alt="" />}
                    <div className="flex-1">
                      <div className="text-sm font-bold text-slate-900">{it.course?.course_title}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{it.course?.course_code}</div>
                      <div className="mt-2 text-sm font-bold text-[#101876]">{inr(it.final_amount)}</div>
                    </div>
                    <button type="button" onClick={() => remove(it)} data-testid={`cart-remove-${it.id}`} className="self-start text-slate-400 hover:text-rose-600"><X className="h-4 w-4" /></button>
                  </div>
                  {plans.length > 0 && (
                    <div className={`mt-3 rounded-xl border-2 border-dashed p-3 text-xs ${isEmi ? "border-emerald-400 bg-emerald-50" : "border-slate-200 bg-slate-50"}`} data-testid={`emi-toggle-${it.course_id}`}>
                      <label className="flex items-start gap-2 cursor-pointer">
                        <input type="checkbox" checked={isEmi} onChange={() => toggleEmi(it.course_id)}
                               disabled={dimmed}
                               className="mt-0.5 h-4 w-4 accent-emerald-600"
                               data-testid={`emi-check-${it.course_id}`} />
                        <div className="flex-1">
                          <div className="font-bold text-slate-900">Pay in EMI</div>
                          <div className="text-slate-600 mt-0.5">
                            {inr(activePlan.down_payment_amount)} down + {activePlan.number_of_installments - 1} × {inr(activePlan.installment_amount)} {activePlan.frequency === "monthly" ? "monthly" : activePlan.frequency}
                            {" · total "}{inr(activePlan.total_fee)}
                          </div>
                          {isEmi && plans.length > 1 && (
                            <select value={emiPlanId} onChange={(e) => setEmiPlanId(e.target.value)}
                                    className="mt-2 w-full rounded-lg border border-emerald-300 bg-white px-2 py-1 text-xs"
                                    data-testid={`emi-plan-select-${it.course_id}`}>
                              {plans.map((p) => (
                                <option key={p.id} value={p.id}>
                                  {p.plan_name} — {inr(p.down_payment_amount)} + {p.number_of_installments - 1}×{inr(p.installment_amount)}
                                </option>
                              ))}
                            </select>
                          )}
                        </div>
                      </label>
                    </div>
                  )}
                  {dimmed && (
                    <div className="mt-2 rounded-md bg-amber-50 px-2 py-1 text-[10px] font-semibold text-amber-800">
                      EMI mode ON for another course — this item rahega next checkout ke liye.
                    </div>
                  )}
                </div>
                );
              })}
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-5 h-fit sticky top-24" data-testid="cart-summary">
              <div className="text-sm font-bold text-slate-900 mb-3">Order summary</div>
              {emiMode && emiPlan ? (
                <div className="space-y-2 text-sm" data-testid="cart-summary-emi">
                  <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 space-y-1">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-800">EMI checkout</div>
                    <div className="text-xs text-slate-700 truncate">{emiCourse?.course?.course_title}</div>
                    <div className="text-[11px] text-slate-500">{emiPlan.plan_name}</div>
                  </div>
                  <div className="flex justify-between pt-1">
                    <span className="text-slate-600">Course fee</span>
                    <span>{inr(emiPlan.total_fee)}</span>
                  </div>
                  {discount && (
                    <div className="flex justify-between text-emerald-700" data-testid="cart-coupon-line">
                      <span className="flex items-center gap-1">
                        Coupon ({coupon})
                        <button type="button" onClick={() => { setCoupon(""); setDiscount(null); setAppliedCoupon(""); toast.success("Coupon removed."); }}
                          className="ml-1 rounded-full p-0.5 text-emerald-600 hover:bg-emerald-100 hover:text-emerald-900"
                          data-testid="cart-remove-coupon"><X className="h-3.5 w-3.5" /></button>
                      </span>
                      <span>-{inr(discount.discount_amount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between border-t border-slate-100 pt-2 mt-1 font-bold text-slate-900">
                    <span>Pay today <span className="text-[10px] font-normal text-slate-500">(down payment)</span></span>
                    <span className="text-emerald-700 text-lg">{inr(emiPlan.down_payment_amount)}</span>
                  </div>
                  <div className="mt-2 rounded-lg border border-slate-200 bg-slate-50 p-2.5 text-[11px] text-slate-700">
                    <div className="font-bold uppercase tracking-widest text-slate-500 text-[10px] mb-1">Future installments</div>
                    {Array.from({ length: emiPlan.number_of_installments - 1 }).map((_, i) => {
                      const isLast = i === emiPlan.number_of_installments - 2;
                      const amt = isLast && discount ? emiLastInstallmentAfterCoupon : emiPlan.installment_amount;
                      return (
                        <div key={i} className="flex justify-between py-0.5">
                          <span>Installment {i + 2}</span>
                          <span className={isLast && discount ? "font-bold text-emerald-700" : ""}>
                            {inr(amt)}{isLast && discount ? <span className="text-[10px] font-normal text-slate-500"> (coupon applied)</span> : ""}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                  <div className="mt-1 text-[10px] text-slate-500 italic">Coupon discount last installment me adjust hoga, down payment par nahi.</div>
                </div>
              ) : (
              <div className="space-y-1.5 text-sm">
                <div className="flex justify-between"><span className="text-slate-600">Subtotal</span><span>{inr(subtotal)}</span></div>
                {discount && (
                  <div className="flex justify-between text-emerald-700" data-testid="cart-coupon-line">
                    <span className="flex items-center gap-1">
                      Coupon ({coupon})
                      <button
                        type="button"
                        onClick={() => { setCoupon(""); setDiscount(null); setAppliedCoupon(""); toast.success("Coupon removed."); }}
                        className="ml-1 rounded-full p-0.5 text-emerald-600 hover:bg-emerald-100 hover:text-emerald-900"
                        aria-label="Remove coupon"
                        title="Remove coupon"
                        data-testid="cart-remove-coupon"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </span>
                    <span>-{inr(discount.discount_amount)}</span>
                  </div>
                )}
                {previewGst > 0 && (
                  <div className="flex justify-between text-slate-600" data-testid="cart-gst-line">
                    <span>GST ({preview?.gst_pct ?? 18}%)</span>
                    <span>+{inr(previewGst)}</span>
                  </div>
                )}
                <div className="flex justify-between border-t border-slate-100 pt-2 mt-2 font-bold text-slate-900"><span>Total</span><span className="text-[#101876] text-lg">{inr(total)}</span></div>
                {preview?.reverse_gst > 0 && (
                  <div className="text-[10px] text-slate-500 italic" data-testid="cart-inclusive-gst-note">
                    Includes {inr(preview.reverse_gst)} GST already in price.
                  </div>
                )}
              </div>
              )}
              <div className="mt-4 flex gap-2">
                <input type="text" placeholder="Coupon code" value={coupon} onChange={(e) => setCoupon(e.target.value)} data-testid="coupon-input"
                       className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm uppercase" />
                <button type="button" onClick={applyCoupon} data-testid="coupon-apply" className="rounded-lg border border-[#101876] px-3 py-2 text-xs font-bold text-[#101876]">Apply</button>
              </div>
              {availableCoupons.length > 0 && (
                <div className="mt-4 rounded-xl border border-dashed border-slate-200 bg-slate-50 p-3" data-testid="cart-available-coupons">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Available coupons</div>
                  <div className="mt-2 space-y-2">
                    {availableCoupons.slice(0, 4).map((cp) => (
                      <div key={cp.id} className="flex items-center justify-between rounded-lg border border-white bg-white p-2 text-xs" data-testid={`available-coupon-${cp.coupon_code}`}>
                        <div>
                          <div className="font-mono font-bold text-[#101876]">{cp.coupon_code}</div>
                          <div className="text-[10px] text-slate-500">
                            {cp.discount_type === "percentage" ? `${cp.discount_value}% off` : `₹${cp.discount_value} off`}
                            {cp.effective_discount ? ` · Save ${inr(cp.effective_discount)}` : ""}
                          </div>
                        </div>
                        <button type="button" onClick={() => applyFromList(cp.coupon_code)}
                          className="rounded bg-[#329112] px-2.5 py-1 text-[10px] font-bold text-white hover:bg-[#26720d]"
                          data-testid={`apply-coupon-${cp.coupon_code}`}>
                          Apply
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <button type="button" onClick={checkout} data-testid="checkout-btn"
                      className="mt-4 w-full rounded-xl bg-[#329112] py-3 text-sm font-bold text-white hover:bg-[#26720d]">
                {emiMode
                  ? (user ? `Proceed · Pay ${inr(emiPlan?.down_payment_amount || 0)} down` : `Checkout as Guest · Pay ${inr(emiPlan?.down_payment_amount || 0)} down`)
                  : (user ? "Proceed to Checkout" : "Checkout as Guest")}
              </button>
              {!user && (
                <p className="mt-1 text-center text-[10px] text-slate-500">
                  Fresh user? Create your account on the next step. <Link to="/login?next=/cart" className="font-semibold text-[#101876] hover:underline">Already have one? Log in</Link>
                </p>
              )}
              <Link to="/courses" className="mt-3 block text-center text-xs font-semibold text-slate-500 hover:text-[#101876]" data-testid="continue-browsing">
                ← Continue browsing
              </Link>
            </div>
          </div>
        )}
      </div>

      {billingOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" role="dialog" aria-modal="true" data-testid="billing-modal">
          <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl">
            <div className="flex items-start justify-between border-b border-slate-100 p-5">
              <div>
                <h2 className="text-lg font-bold text-slate-900">{user ? "Billing details" : "Complete your purchase"}</h2>
                <p className="mt-1 text-xs text-slate-500">
                  {user
                    ? "Required for GST invoice · we save these for next time."
                    : "Create your account + billing in one step. Already registered? "}
                  {!user && <Link to="/login?next=/cart" className="font-semibold text-[#101876] hover:underline">Log in →</Link>}
                </p>
              </div>
              <button type="button" onClick={() => setBillingOpen(false)} className="rounded-lg p-1 text-slate-400 hover:bg-slate-100" data-testid="billing-close" aria-label="Close">
                <XIcon />
              </button>
            </div>
            <div className="grid gap-3 p-5 sm:grid-cols-2 max-h-[70vh] overflow-y-auto">
              {!user && (
                <>
                  <div className="sm:col-span-2 rounded-lg bg-slate-50 px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-slate-500">Your account</div>
                  <BillingField label="First name *" value={signup.first_name} onChange={(v) => setSignup({ ...signup, first_name: v })} testId="signup-first-name" />
                  <BillingField label="Last name *" value={signup.last_name} onChange={(v) => setSignup({ ...signup, last_name: v })} testId="signup-last-name" />
                  <BillingField label="Password *" value={signup.password} onChange={(v) => setSignup({ ...signup, password: v })} testId="signup-password" type="password" />
                  <BillingField label="Confirm password *" value={signup.confirm_password} onChange={(v) => setSignup({ ...signup, confirm_password: v })} testId="signup-confirm-password" type="password" />
                  <div className="sm:col-span-2 rounded-lg bg-slate-50 px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-slate-500 mt-2">Billing address</div>
                </>
              )}
              {user && (
                <div className="sm:col-span-2">
                  <BillingField label="Full name *" value={billing.billing_name} onChange={(v) => setBilling({ ...billing, billing_name: v })} testId="billing-name" />
                </div>
              )}
              <BillingField label="Email *" value={billing.billing_email} onChange={(v) => setBilling({ ...billing, billing_email: v })} testId="billing-email" type="email" />
              <BillingField label="Mobile *" value={billing.billing_mobile} onChange={(v) => setBilling({ ...billing, billing_mobile: v })} testId="billing-mobile" />
              <BillingField label="City *" value={billing.billing_city} onChange={(v) => setBilling({ ...billing, billing_city: v })} testId="billing-city" />
              <BillingField label="Pincode *" value={billing.billing_pincode} onChange={(v) => setBilling({ ...billing, billing_pincode: v })} testId="billing-pincode" />
              <div className="sm:col-span-2">
                <BillingField label="Address *" value={billing.billing_address} onChange={(v) => setBilling({ ...billing, billing_address: v })} testId="billing-address" />
              </div>
              <BillingField label="State *" value={billing.billing_state} onChange={(v) => setBilling({ ...billing, billing_state: v })} testId="billing-state" />
              <BillingField label="Country" value={billing.billing_country} onChange={(v) => setBilling({ ...billing, billing_country: v })} testId="billing-country" />
              <div className="sm:col-span-2">
                <BillingField label="GSTIN (optional)" value={billing.billing_gst_number} onChange={(v) => setBilling({ ...billing, billing_gst_number: v })} testId="billing-gst" />
              </div>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 bg-slate-50 p-4">
              <div className="text-xs text-slate-500">
                {emiMode && emiPlan
                  ? <>Pay today: <b className="text-emerald-700">{inr(emiPlan.down_payment_amount)}</b> <span className="text-[10px]">(down payment)</span></>
                  : <>Amount payable: <b className="text-slate-900">{inr(total)}</b></>}
              </div>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => setBillingOpen(false)} className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100" data-testid="billing-cancel">Cancel</button>
                <button type="button" onClick={submitCheckout} className="rounded-xl bg-[#329112] px-5 py-2 text-sm font-bold text-white hover:bg-[#26720d]" data-testid="billing-submit">
                  {emiMode && emiPlan
                    ? (user ? `Pay ${inr(emiPlan.down_payment_amount)} down & enroll` : `Create account & Pay ${inr(emiPlan.down_payment_amount)} down`)
                    : (user ? `Pay ${inr(total)}` : `Create account & Pay ${inr(total)}`)}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function BillingField({ label, value, onChange, testId, type = "text" }) {
  return (
    <label className="block">
      <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-[#101876] focus:outline-none focus:ring-2 focus:ring-[#101876]/20"
        data-testid={testId}
      />
    </label>
  );
}

function XIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
  );
}
